import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useApp } from "@/lib/store";
import api from "@/lib/api";
import {
    Loader2,
    Save,
    ShieldCheck,
    Bot,
    Plug,
    Trash2,
    Plus,
    RefreshCw,
    MessageCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "@/components/ui/tabs";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const MODES = ["SUGGEST", "APPROVAL", "AUTOPILOT"];
const MODE_COPY = {
    SUGGEST: "AI drafts silently — you decide whether to use it.",
    APPROVAL: "AI drafts and holds for human approval before publishing.",
    AUTOPILOT: "AI publishes automatically (safety override still applies).",
};

export default function Settings() {
    const { currentBusiness } = useApp();
    const [memory, setMemory] = useState(null);
    const [autonomy, setAutonomy] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [integrations, setIntegrations] = useState(null);
    const [params, setParams] = useSearchParams();
    const [tab, setTab] = useState(params.get("tab") || "autonomy");
    const loadedFor = useRef(null);

    // Coming back from Instagram login (see /api/instagram/oauth/callback).
    useEffect(() => {
        const ig = params.get("instagram");
        if (!ig) return;
        const reason = params.get("reason") || "";
        if (ig === "connected") toast.success("Instagram connected.");
        else if (ig === "cancelled") toast.info("Instagram connection cancelled.");
        else toast.error(`Instagram connection failed${reason ? `: ${reason.replaceAll("_", " ")}` : "."}`);
        setTab("integrations");
        setParams({}, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Links such as the header's mode badge (/settings?tab=autonomy) switch tabs in place.
    const tabParam = params.get("tab");
    useEffect(() => {
        if (tabParam && !params.get("instagram")) setTab(tabParam);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabParam]);

    // First load shows a spinner; refreshes after an action keep the page (and tab) in place.
    const load = async () => {
        if (!currentBusiness) return;
        const firstLoad = loadedFor.current !== currentBusiness.id;
        if (firstLoad) setLoading(true);
        try {
            const [{ autonomy: a }, ints] = await Promise.all([
                api.autonomy(currentBusiness.id),
                api.integrations(currentBusiness.id),
            ]);
            const { memory: m } = await api.memory(currentBusiness.id);
            setMemory(m);
            setAutonomy(a);
            setIntegrations(ints.integrations || []);
            loadedFor.current = currentBusiness.id;
        } catch {
            toast.error("Couldn't load settings.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentBusiness?.id]);

    const saveMemory = async () => {
        setSaving(true);
        try {
            await api.updateMemory(currentBusiness.id, {
                brand_voice: memory.brand_voice,
                tone: memory.tone,
                review_response_style: memory.review_response_style,
                approved_claims: memory.approved_claims || [],
                prohibited_claims: memory.prohibited_claims || [],
                services: memory.services || [],
                keywords: memory.keywords || [],
                business_goals: memory.business_goals || [],
            });
            toast.success("AI memory saved.");
        } catch {
            toast.error("Save failed.");
        } finally {
            setSaving(false);
        }
    };

    const updateAutonomy = async (field, value) => {
        try {
            const { autonomy: a } = await api.updateAutonomy(currentBusiness.id, {
                [field]: value,
            });
            setAutonomy(a);
            window.dispatchEvent(new CustomEvent("autonomy-changed", { detail: a }));
            toast.success(`${field.replaceAll("_", " ")} → ${value}`);
        } catch {
            toast.error("Update failed.");
        }
    };

    if (loading || !memory || !autonomy)
        return (
            <div className="text-center py-16 text-slate-500">
                <Loader2 className="h-6 w-6 animate-spin mx-auto" />
            </div>
        );

    return (
        <div className="max-w-5xl mx-auto space-y-6 fade-in" data-testid="settings-page">
            <div>
                <div className="label-eyebrow text-indigo-300">Settings</div>
                <h1 className="mt-1 text-3xl font-extrabold text-slate-50 tracking-tight">
                    AI Memory, Autonomy &amp; Integrations
                </h1>
            </div>
            <Tabs value={tab} onValueChange={setTab} className="w-full">
                <TabsList className="bg-white/[0.03] border border-white/10">
                    <TabsTrigger value="autonomy" data-testid="settings-tab-autonomy">
                        Autonomy
                    </TabsTrigger>
                    <TabsTrigger value="memory" data-testid="settings-tab-memory">
                        AI Memory
                    </TabsTrigger>
                    <TabsTrigger
                        value="integrations"
                        data-testid="settings-tab-integrations"
                    >
                        Integrations
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="autonomy" className="mt-6 space-y-4">
                    {[
                        { k: "review_replies", l: "Review replies" },
                        { k: "complaints", l: "Complaints" },
                        { k: "reports", l: "Reports" },
                        { k: "memory_updates", l: "Memory updates" },
                    ].map((row) => (
                        <div
                            key={row.k}
                            className="surface rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                        >
                            <div className="flex-1">
                                <div className="flex items-center gap-2">
                                    <ShieldCheck className="h-4 w-4 text-slate-500" />
                                    <div className="text-sm font-semibold text-slate-100">
                                        {row.l}
                                    </div>
                                </div>
                                <div className="mt-1 text-xs text-slate-400">
                                    {MODE_COPY[autonomy[row.k]]}
                                </div>
                            </div>
                            <div className="flex items-center gap-1 bg-white/[0.03] border border-white/10 rounded-xl p-1">
                                {MODES.map((m) => (
                                    <button
                                        key={m}
                                        onClick={() => updateAutonomy(row.k, m)}
                                        data-testid={`autonomy-toggle-${row.k}-${m.toLowerCase()}`}
                                        className={`text-[11px] font-semibold px-3 py-1.5 rounded-lg transition-colors ${
                                            autonomy[row.k] === m
                                                ? "bg-indigo-500 text-white"
                                                : "text-slate-400 hover:text-slate-100"
                                        }`}
                                    >
                                        {m}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                    <div className="surface rounded-2xl p-4 border-l-2 border-l-rose-500/60">
                        <div className="text-xs text-rose-200 flex items-center gap-2">
                            <ShieldCheck className="h-3.5 w-3.5" />
                            <span className="font-semibold">Safety override:</span> reviews
                            containing legal, injury, negligence, fraud, harassment,
                            privacy, or medical content ALWAYS require human approval —
                            regardless of your autonomy settings.
                        </div>
                    </div>
                </TabsContent>

                <TabsContent value="memory" className="mt-6">
                    <div className="surface rounded-2xl p-6 space-y-5">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <Label className="text-xs text-slate-400">Brand voice</Label>
                                <Textarea
                                    data-testid="memory-brand-voice-input"
                                    value={memory.brand_voice || ""}
                                    onChange={(e) =>
                                        setMemory({ ...memory, brand_voice: e.target.value })
                                    }
                                    rows={2}
                                    className="mt-1.5 bg-white/[0.03] border-white/10 text-slate-100"
                                />
                            </div>
                            <div>
                                <Label className="text-xs text-slate-400">Tone</Label>
                                <Input
                                    data-testid="memory-tone-input"
                                    value={memory.tone || ""}
                                    onChange={(e) =>
                                        setMemory({ ...memory, tone: e.target.value })
                                    }
                                    className="mt-1.5 bg-white/[0.03] border-white/10 text-slate-100"
                                />
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs text-slate-400">
                                Review response style
                            </Label>
                            <Textarea
                                data-testid="memory-response-style-input"
                                value={memory.review_response_style || ""}
                                onChange={(e) =>
                                    setMemory({
                                        ...memory,
                                        review_response_style: e.target.value,
                                    })
                                }
                                rows={2}
                                className="mt-1.5 bg-white/[0.03] border-white/10 text-slate-100"
                            />
                        </div>

                        <ClaimsEditor
                            title="Approved claims (facts AI may use in replies)"
                            testid="approved-claims"
                            items={memory.approved_claims || []}
                            onChange={(v) =>
                                setMemory({ ...memory, approved_claims: v })
                            }
                            color="emerald"
                        />

                        <ClaimsEditor
                            title="Prohibited claims (never appear in replies)"
                            testid="prohibited-claims"
                            items={memory.prohibited_claims || []}
                            onChange={(v) =>
                                setMemory({ ...memory, prohibited_claims: v })
                            }
                            color="rose"
                        />

                        <div className="flex justify-end pt-2 border-t border-white/5">
                            <Button
                                onClick={saveMemory}
                                disabled={saving}
                                data-testid="memory-save-button"
                                className="bg-indigo-500 hover:bg-indigo-400 text-white gap-1.5"
                            >
                                {saving ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <Save className="h-4 w-4" />
                                )}
                                Save AI memory
                            </Button>
                        </div>
                    </div>
                </TabsContent>

                <TabsContent value="integrations" className="mt-6 space-y-3">
                    {integrations && integrations.map((c) =>
                        c.provider === "whatsapp" ? (
                            <WhatsAppCard
                                key={c.provider}
                                card={c}
                                businessId={currentBusiness.id}
                                onChange={load}
                            />
                        ) : (
                            <IntegrationCard
                                key={c.provider}
                                card={c}
                                businessId={currentBusiness.id}
                                onChange={load}
                            />
                        ))}
                </TabsContent>
            </Tabs>
        </div>
    );
}

function IntegrationCard({ card, businessId, onChange }) {
    const [busy, setBusy] = useState(null);
    const run = async (fn, kind) => {
        setBusy(kind);
        try {
            const result = await fn();
            if (result?.authorization_url) {
                window.location.assign(result.authorization_url);
                return;
            }
            if (result?.status === "synced") {
                toast.success(`${card.label} synced — ${result.new} new comment${result.new === 1 ? "" : "s"}.`);
            } else {
                toast.success(isLive ? `${card.label} · ${kind}` : `${card.label} · ${kind} (demo)`);
            }
            onChange?.();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            const friendly = {
                instagram_oauth_not_configured:
                    "Instagram login isn't set up on the server yet (INSTAGRAM_APP_ID, INSTAGRAM_APP_SECRET, INSTAGRAM_REDIRECT_URI, TOKEN_ENCRYPTION_KEY).",
                instagram_redirect_requires_https: "INSTAGRAM_REDIRECT_URI must start with https://.",
                invalid_token_encryption_key: "TOKEN_ENCRYPTION_KEY on the server isn't a valid Fernet key.",
                instagram_token_expired: "Instagram login expired — click Reconnect.",
                instagram_not_connected: "Instagram isn't connected — click Reconnect.",
            }[detail];
            toast.error(friendly || (typeof detail === "string" ? detail : `${kind} failed`));
        } finally {
            setBusy(null);
        }
    };
    const isConnected = card.status === "connected";
    const isLive = card.mode === "live";
    return (
        <>
        <div
            data-testid={`integration-card-${card.provider}`}
            className="surface rounded-2xl p-5 flex items-start gap-4"
        >
            <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                <Plug className="h-4 w-4 text-slate-400" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-slate-100">{card.label}</span>
                    <Badge
                        variant="outline"
                        data-testid={`integration-status-${card.provider}`}
                        className={
                            isConnected
                                ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
                                : "border-white/10 text-slate-400"
                        }
                    >
                        {isConnected ? (isLive ? "Connected · Live" : "Connected · Demo") : "Not connected"}
                    </Badge>
                    {isConnected && !isLive && (
                        <Badge className="bg-amber-500/15 border border-amber-500/30 text-amber-200 text-[10px]">
                            {card.label_hint || "Demo connection"}
                        </Badge>
                    )}
                </div>
                <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-[11px] text-slate-500">
                    <div>
                        <span className="mono uppercase tracking-widest text-slate-600">Account</span>
                        <div className="text-slate-300">{card.account_name || "—"}</div>
                    </div>
                    <div>
                        <span className="mono uppercase tracking-widest text-slate-600">Company</span>
                        <div className="text-slate-300">{card.connected_company}</div>
                    </div>
                    <div>
                        <span className="mono uppercase tracking-widest text-slate-600">Last sync</span>
                        <div className="text-slate-300">
                            {card.last_sync_at ? new Date(card.last_sync_at).toLocaleString() : "—"}
                        </div>
                    </div>
                    <div>
                        <span className="mono uppercase tracking-widest text-slate-600">Permissions</span>
                        <div className="text-slate-300">
                            {(card.permissions_available || []).slice(0, 2).join(", ")}
                        </div>
                    </div>
                </div>
            </div>
            <div className="flex flex-col gap-1.5 shrink-0">
                {!isConnected ? (
                    <Button
                        size="sm"
                        onClick={() => run(() => api.connectIntegration(businessId, card.provider), "connect")}
                        disabled={busy === "connect"}
                        data-testid={`integration-connect-${card.provider}`}
                        className="bg-indigo-500 hover:bg-indigo-400 text-white gap-1.5 h-8"
                    >
                        {busy === "connect" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plug className="h-3 w-3" />}
                        {card.provider === "instagram"
    ? "Connect Instagram"
    : card.provider === "google_business_profile"
      ? "Connect Google Business"
      : "Connect (demo)"}
                    </Button>
                ) : (
                    <>
                        {(!isLive || card.provider === "instagram") && (
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => run(() => api.syncIntegration(businessId, card.provider), "sync")}
                            disabled={busy === "sync"}
                            data-testid={`integration-sync-${card.provider}`}
                            className="border-white/10 bg-white/5 hover:bg-white/10 text-slate-100 gap-1.5 h-8"
                        >
                            {busy === "sync" ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                            Sync now
                        </Button>
                        )}
                        {card.provider !== "google_business_profile" && <Button
                            size="sm"
                            variant="outline"
                            onClick={() => run(() => api.reconnectIntegration(businessId, card.provider), "reconnect")}
                            disabled={busy === "reconnect"}
                            data-testid={`integration-reconnect-${card.provider}`}
                            className="border-white/10 bg-white/5 hover:bg-white/10 text-slate-100 gap-1.5 h-8"
                        >
                            <Plug className="h-3 w-3" />
                            Reconnect
                        </Button>}
                        {card.provider !== "google_business_profile" && <Button
                            size="sm"
                            variant="outline"
                            onClick={() => run(() => api.disconnectIntegration(businessId, card.provider), "disconnect")}
                            disabled={busy === "disconnect"}
                            data-testid={`integration-disconnect-${card.provider}`}
                            className="border-rose-500/30 bg-rose-500/10 text-rose-200 hover:bg-rose-500/20 gap-1.5 h-8"
                        >
                            <Trash2 className="h-3 w-3" />
                            Disconnect
                        </Button>}
                    </>
                )}
            </div>
        </div>
            {card.provider === "google_business_profile" && isConnected && (
                <GBPLocationSelector businessId={businessId} />
            )}
        </>
    );
}



function GBPLocationSelector({ businessId }) {
    const [locations, setLocations] = useState([]);
    const [selected, setSelected] = useState("");
    const [saved, setSaved] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!businessId) return;

        let alive = true;
        setLoading(true);
        setError("");

        Promise.all([
            api.gbpLocations(businessId),
            api.gbpLocationBinding(businessId),
        ])
            .then(([locationData, bindingData]) => {
                if (!alive) return;

                const available = locationData.locations || [];
                const current =
                    bindingData.binding?.google_location_name || "";

                setLocations(available);
                setSaved(current);
                setSelected(current);
            })
            .catch((e) => {
                if (!alive) return;
                setError(
                    e?.response?.data?.detail ||
                    "Unable to load Google Business locations."
                );
            })
            .finally(() => {
                if (alive) setLoading(false);
            });

        return () => {
            alive = false;
        };
    }, [businessId]);

    const saveLocation = async () => {
        if (!selected || saving) return;

        setSaving(true);
        setError("");

        try {
            await api.gbpSaveLocation(businessId, selected);
            setSaved(selected);
            toast.success("Google Business location selected.");
        } catch (e) {
            const detail = e?.response?.data?.detail;
            setError(
                typeof detail === "string"
                    ? detail
                    : "Could not save Google Business location."
            );
        } finally {
            setSaving(false);
        }
    };

    const savedLocation = locations.find(
        (loc) => loc.name === saved
    );

    return (
        <div className="mt-3 rounded-xl border border-white/10 bg-white/5 p-4 space-y-3">
            <div>
                <div className="font-semibold text-slate-100">
                    Select Google Business Location
                </div>
                <div className="text-xs text-slate-400 mt-1">
                    Only the selected location will be linked to this business.
                </div>
            </div>

            {loading ? (
                <div className="text-sm text-slate-400">
                    Loading Google locations...
                </div>
            ) : (
                <>
                    <select
                        value={selected}
                        onChange={(e) => setSelected(e.target.value)}
                        className="w-full rounded-lg border border-white/20 bg-slate-900 text-slate-100 p-3 text-sm"
                    >
                        <option value="">Choose a business location</option>
                        {locations.map((loc) => (
                            <option key={loc.name} value={loc.name}>
                                {loc.title || loc.name}
                            </option>
                        ))}
                    </select>

                    <button
                        type="button"
                        onClick={saveLocation}
                        disabled={!selected || saving || selected === saved}
                        className="rounded-lg bg-indigo-500 hover:bg-indigo-400 disabled:opacity-50 px-4 py-2 text-white text-sm"
                    >
                        {saving ? "Saving..." : "Save Location"}
                    </button>

                    {saved && (
                        <div className="text-sm text-emerald-400">
                            Selected: {savedLocation?.title || saved}
                        </div>
                    )}
                </>
            )}

            {error && (
                <div className="text-sm text-rose-400">
                    {String(error)}
                </div>
            )}

            {saved && (
                <GBPSyncStatus
                    businessId={businessId}
                    locationName={saved}
                />
            )}
        </div>
    );
}


function GBPSyncStatus({ businessId, locationName }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!businessId || !locationName) return;

        let active = true;
        setLoading(true);
        setData(null);
        setError("");

        api.gbpSyncStatus(businessId)
            .then((response) => {
                if (active) setData(response);
            })
            .catch(() => {
                if (active) {
                    setError("Could not load Google reviews sync status.");
                }
            })
            .finally(() => {
                if (active) setLoading(false);
            });

        return () => { active = false; };
    }, [businessId, locationName]);

    const sync = data?.sync;
    const result = sync?.last_result;
    const matchesLocation =
        data?.location?.google_location_name === locationName;

    const lastSync = sync?.last_success_at
        ? new Date(sync.last_success_at)
        : null;

    const lastSyncText = lastSync && !Number.isNaN(lastSync.getTime())
        ? lastSync.toLocaleString("en-IN", {
            dateStyle: "medium",
            timeStyle: "short",
        })
        : "Not synced yet";

    return (
        <div className="mt-4 rounded-xl border border-white/10 bg-slate-900/60 p-4 space-y-3">
            <div className="flex items-center justify-between gap-3">
                <div className="font-semibold text-slate-100">
                    Google Reviews Sync
                </div>
                {!loading && matchesLocation && (
                    <span className={
                        sync?.status === "success"
                            ? "text-xs text-emerald-400"
                            : sync?.status === "failed"
                                ? "text-xs text-rose-400"
                                : "text-xs text-amber-400"
                    }>
                        {sync?.status === "success"
                            ? "Success"
                            : sync?.status === "failed"
                                ? "Failed"
                                : "Pending"}
                    </span>
                )}
            </div>

            {loading && (
                <p className="text-sm text-slate-400">
                    Loading sync status...
                </p>
            )}

            {error && (
                <p className="text-sm text-rose-400">{error}</p>
            )}

            {!loading && !error && !matchesLocation && (
                <p className="text-sm text-slate-400">
                    Sync status is not available for this location.
                </p>
            )}

            {!loading && !error && matchesLocation && (
                <>
                    <div className="text-xs text-slate-400">
                        Last successful sync: {lastSyncText}
                    </div>

                    {result && (
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            {[
                                ["Fetched", result.fetched],
                                ["New", result.new],
                                ["Updated", result.updated],
                                ["Unchanged", result.unchanged],
                            ].map(([label, value]) => (
                                <div
                                    key={label}
                                    className="rounded-lg bg-white/5 p-3 text-center"
                                >
                                    <div className="text-lg font-bold text-white">
                                        {value ?? "—"}
                                    </div>
                                    <div className="text-xs text-slate-400">
                                        {label}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {sync?.status === "failed" && (
                        <p className="text-xs text-rose-400">
                            Sync failed: {sync.last_error_type || "Unknown error"}
                        </p>
                    )}
                </>
            )}
        </div>
    );
}


function WhatsAppCard({ card, businessId, onChange }) {
    const [conn, setConn] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({
        phone_number_id: "",
        business_account_id: "",
        access_token: "",
        display_number: "",
        app_secret: "",
    });

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.waConnection(businessId);
            setConn(r.connection);
        } catch { /* ignore */ }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [businessId]); // eslint-disable-line

    const save = async () => {
        setBusy("save");
        try {
            const r = await api.waSaveConnection(businessId, form);
            setConn(r.connection);
            toast.success("Credentials saved (encrypted).");
            setShowForm(false);
            setForm({ ...form, access_token: "", app_secret: "" });
            onChange?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed.");
        } finally { setBusy(null); }
    };
    const verify = async () => {
        setBusy("verify");
        try {
            const r = await api.waVerify(businessId);
            setConn(r.connection);
            if (r.result?.ok) {
                toast.success(`Live — verified with Meta (${r.result.meta_payload?.display_phone_number || "OK"})`);
            } else {
                toast.error(`Verification failed: ${r.result?.error || "unknown"}`);
            }
        } catch { toast.error("Verify call failed."); }
        finally { setBusy(null); }
    };
    const disconnect = async () => {
        setBusy("disconnect");
        try {
            const r = await api.waDisconnect(businessId);
            setConn(r.connection);
            toast.success("Disconnected.");
            onChange?.();
        } catch { toast.error("Disconnect failed."); }
        finally { setBusy(null); }
    };

    const isConnected = conn?.status === "connected";
    const isError = conn?.status === "error";
    const backend = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    const webhookUrl = `${backend}/api/webhooks/whatsapp/${businessId}`;

    return (
        <div data-testid="integration-card-whatsapp"
             className="surface rounded-2xl p-5">
            <div className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center shrink-0">
                    <MessageCircle className="h-4 w-4 text-emerald-300" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-slate-100">WhatsApp Business API</span>
                        <Badge
                            variant="outline"
                            data-testid="integration-status-whatsapp"
                            className={
                                isConnected
                                    ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
                                    : isError
                                    ? "border-rose-500/30 bg-rose-500/10 text-rose-200"
                                    : "border-white/10 text-slate-400"
                            }>
                            {isConnected ? "Live" : isError ? "Error" : "Simulation"}
                        </Badge>
                        <Badge className="bg-amber-500/15 border border-amber-500/30 text-amber-200 text-[10px]"
                               data-testid="whatsapp-honesty-label">
                            {conn?.honesty_label || "Simulation — connect WhatsApp Business API to go live"}
                        </Badge>
                    </div>
                    <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-[11px] text-slate-500">
                        <div>
                            <span className="mono uppercase tracking-widest text-slate-600">Phone No. ID</span>
                            <div className="text-slate-300 mono">{conn?.phone_number_id || "—"}</div>
                        </div>
                        <div>
                            <span className="mono uppercase tracking-widest text-slate-600">Access token</span>
                            <div className="text-slate-300 mono" data-testid="wa-token-masked">{conn?.access_token_masked || "—"}</div>
                        </div>
                        <div>
                            <span className="mono uppercase tracking-widest text-slate-600">Display #</span>
                            <div className="text-slate-300">{conn?.meta_display_phone_number || conn?.display_number || "—"}</div>
                        </div>
                        <div>
                            <span className="mono uppercase tracking-widest text-slate-600">Quality</span>
                            <div className="text-slate-300">{conn?.meta_quality_rating || "—"}</div>
                        </div>
                    </div>
                    {conn?.phone_number_id && !conn?.app_secret_set && (
                        <div className="mt-3 rounded-xl bg-amber-500/[0.06] border border-amber-500/20 p-2.5 text-[11px] text-amber-200"
                             data-testid="wa-secret-missing">
                            Incoming WhatsApp messages are refused until the Meta app secret is added
                            (Update credentials → Meta app secret). It proves messages really come from Meta.
                        </div>
                    )}
                    {isError && conn?.verification_error && (
                        <div className="mt-3 rounded-xl bg-rose-500/[0.06] border border-rose-500/20 p-2.5 text-[11px] text-rose-200"
                             data-testid="wa-verification-error">
                            Meta reported: {conn.verification_error}
                        </div>
                    )}
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                    <Button size="sm" onClick={() => setShowForm(!showForm)}
                        data-testid="wa-edit-creds"
                        className="bg-indigo-500 hover:bg-indigo-400 text-white gap-1.5 h-8">
                        <Plug className="h-3 w-3" />
                        {conn?.phone_number_id ? "Update credentials" : "Add credentials"}
                    </Button>
                    {conn?.phone_number_id && (
                        <>
                            <Button size="sm" variant="outline" onClick={verify} disabled={busy === "verify"}
                                data-testid="wa-verify-btn"
                                className="border-white/10 bg-white/5 hover:bg-white/10 text-slate-100 gap-1.5 h-8">
                                {busy === "verify" ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldCheck className="h-3 w-3" />}
                                Verify with Meta
                            </Button>
                            <Button size="sm" variant="outline" onClick={disconnect} disabled={busy === "disconnect"}
                                data-testid="wa-disconnect-btn"
                                className="border-rose-500/30 bg-rose-500/10 text-rose-200 hover:bg-rose-500/20 gap-1.5 h-8">
                                <Trash2 className="h-3 w-3" /> Disconnect
                            </Button>
                        </>
                    )}
                </div>
            </div>

            {showForm && (
                <div className="mt-4 pt-4 border-t border-white/5 space-y-3" data-testid="wa-form">
                    <div className="text-[11px] text-slate-400 flex items-center gap-2">
                        <ShieldCheck className="h-3 w-3 text-emerald-300" />
                        The access token is stored encrypted at rest and never returned to the browser. You'll see only the last 4 characters after saving.
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs text-slate-400">Phone Number ID</Label>
                            <Input data-testid="wa-input-phone-id" value={form.phone_number_id}
                                onChange={(e) => setForm({ ...form, phone_number_id: e.target.value })}
                                placeholder="e.g. 105954558508523"
                                className="mt-1 bg-white/[0.03] border-white/10 text-slate-100" />
                        </div>
                        <div>
                            <Label className="text-xs text-slate-400">WhatsApp Business Account ID</Label>
                            <Input data-testid="wa-input-waba-id" value={form.business_account_id}
                                onChange={(e) => setForm({ ...form, business_account_id: e.target.value })}
                                placeholder="e.g. 102290129340398"
                                className="mt-1 bg-white/[0.03] border-white/10 text-slate-100" />
                        </div>
                        <div className="md:col-span-2">
                            <Label className="text-xs text-slate-400">System-user access token</Label>
                            <Input data-testid="wa-input-token" type="password" value={form.access_token}
                                onChange={(e) => setForm({ ...form, access_token: e.target.value })}
                                placeholder="EAA..."
                                className="mt-1 bg-white/[0.03] border-white/10 text-slate-100 font-mono" />
                        </div>
                        <div>
                            <Label className="text-xs text-slate-400">Display number (optional)</Label>
                            <Input data-testid="wa-input-display-number" value={form.display_number}
                                onChange={(e) => setForm({ ...form, display_number: e.target.value })}
                                placeholder="+1 415 555 0110"
                                className="mt-1 bg-white/[0.03] border-white/10 text-slate-100" />
                        </div>
                        <div className="md:col-span-2">
                            <Label className="text-xs text-slate-400">
                                Meta app secret {conn?.app_secret_set ? "(set — leave blank to keep)" : "(needed to accept live webhooks)"}
                            </Label>
                            <Input data-testid="wa-input-app-secret" type="password" value={form.app_secret}
                                onChange={(e) => setForm({ ...form, app_secret: e.target.value })}
                                placeholder="From Meta App Dashboard → App settings → Basic"
                                className="mt-1 bg-white/[0.03] border-white/10 text-slate-100 font-mono" />
                        </div>
                        <div>
                            <Label className="text-xs text-slate-400">Webhook URL (paste this into Meta)</Label>
                            <Input readOnly value={webhookUrl}
                                data-testid="wa-webhook-url"
                                className="mt-1 bg-white/[0.03] border-white/10 text-slate-300 font-mono text-xs" />
                        </div>
                    </div>
                    {conn?.verify_token && (
                        <div className="rounded-xl bg-white/[0.02] border border-white/10 p-3">
                            <div className="text-[10px] mono uppercase tracking-widest text-slate-500 mb-1">Verify token (paste into Meta webhook config)</div>
                            <code className="text-xs text-slate-300 mono break-all" data-testid="wa-verify-token">{conn.verify_token}</code>
                        </div>
                    )}
                    <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setShowForm(false)}
                            className="border-white/10 bg-white/5 hover:bg-white/10 text-slate-100 h-9">
                            Cancel
                        </Button>
                        <Button onClick={save} disabled={busy === "save" || !form.phone_number_id || !form.access_token}
                            data-testid="wa-save-btn"
                            className="bg-emerald-500 hover:bg-emerald-400 text-white gap-1.5 h-9">
                            {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                            Save credentials
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}

function ClaimsEditor({ title, testid, items, onChange, color }) {    const [draft, setDraft] = useState("");
    const colors = {
        emerald: "bg-emerald-500/10 border-emerald-500/30 text-emerald-200",
        rose: "bg-rose-500/10 border-rose-500/30 text-rose-200",
    };
    return (
        <div>
            <Label className="text-xs text-slate-400">{title}</Label>
            <div className="mt-2 flex flex-wrap gap-2">
                {items.map((c, i) => (
                    <div
                        key={i}
                        data-testid={`${testid}-item-${i}`}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs ${colors[color]}`}
                    >
                        {c}
                        <button
                            onClick={() =>
                                onChange(items.filter((_, j) => j !== i))
                            }
                            data-testid={`${testid}-remove-${i}`}
                        >
                            <Trash2 className="h-3 w-3 opacity-60 hover:opacity-100" />
                        </button>
                    </div>
                ))}
            </div>
            <div className="mt-2 flex items-center gap-2">
                <Input
                    data-testid={`${testid}-input`}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder="Add another…"
                    className="bg-white/[0.03] border-white/10 text-slate-100 h-9 text-sm"
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && draft.trim()) {
                            e.preventDefault();
                            onChange([...items, draft.trim()]);
                            setDraft("");
                        }
                    }}
                />
                <Button
                    variant="outline"
                    data-testid={`${testid}-add-button`}
                    onClick={() => {
                        if (draft.trim()) {
                            onChange([...items, draft.trim()]);
                            setDraft("");
                        }
                    }}
                    className="border-white/10 bg-white/5 hover:bg-white/10 text-slate-100 gap-1.5 h-9"
                >
                    <Plus className="h-3.5 w-3.5" /> Add
                </Button>
            </div>
        </div>
    );
}
